import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { getState, ensure, subscribe } from '../../_utils/receptionMode';

/**
 * Hide a page while this device is in reception mode and any of `areaNames`
 * is hidden (reception audit 2026-10-02, H1).
 *
 *  - Nothing protected renders until the device's state is VERIFIED with the
 *    server for this page load (src/_utils/receptionMode). A device last
 *    known to be locked is treated as locked meanwhile, and stays locked if
 *    the check fails: fail closed.
 *  - Changes (unlock, another tab, revalidation) re-render every guard.
 *
 * The API enforces the same areas (beu-node-v2 receptionAreas.ROUTES); this
 * only keeps the screen honest.
 *
 * @param {Component} WrappedComponent
 * @param {string|string[]} areaNames  hidden if ANY of them is hidden
 */
const ReceptionModeGuard = (WrappedComponent, areaNames) => {
    const areas = Array.isArray(areaNames) ? areaNames : [areaNames];
    const hiddenIn = (s) => !!(s && s.enabled && areas.some((a) => s.hiddenAreas && s.hiddenAreas[a] === true));

    class WithReceptionModeGuard extends Component {
        constructor(props) {
            super(props);
            const s = getState();
            // Verified, or known locked with this area hidden: decide now.
            // Otherwise wait for the server before showing anything.
            this.state = { s, ready: s.verified || hiddenIn(s) };
        }

        componentDidMount() {
            this.mounted = true;
            this.unsubscribe = subscribe(() => this.mounted && this.setState({ s: getState(), ready: true }));
            if (!this.state.s.verified) {
                ensure().then((s) => this.mounted && this.setState({ s, ready: true }));
            }
        }

        componentWillUnmount() {
            this.mounted = false;
            if (this.unsubscribe) this.unsubscribe();
        }

        redirectTarget() {
            const { s } = this.state;
            const h = (s && s.hiddenAreas) || {};
            const pathname = (this.props.location && this.props.location.pathname) || '';
            const hd = this.props.user && this.props.user.userInfo && this.props.user.userInfo.hairdresser;
            const isSalonOwner = hd && hd.isSalonOwner === 1;
            const has = (a) => areas.includes(a);
            // A hidden catalogue tab → the first visible sibling.
            if (pathname === '/new-services' || pathname === '/product' || pathname === '/stocktake') {
                if (!h.catalogue) {
                    if (isSalonOwner && !h.stocktake && pathname !== '/stocktake') return '/stocktake';
                    if (isSalonOwner && !h.products && pathname !== '/product') return '/product';
                    if (!h.services && pathname !== '/new-services') return '/new-services';
                }
                return '/';
            }
            // A hidden settings tab → Reception mode settings (always visible
            // while Settings is), where the owner can unlock.
            if (pathname.startsWith('/settings/') && !h.settings && isSalonOwner && pathname !== '/settings/reception-mode') return '/settings/reception-mode';
            // A hidden Team tab → the first visible Team tab.
            if (pathname.startsWith('/staff') && (has('teamMembers') || has('schedules') || has('staffPay')) && !h.staff) {
                if (!h.teamMembers && pathname !== '/staff') return '/staff';
                if (!h.schedules && pathname !== '/staff/schedules') return '/staff/schedules';
                if (!h.staffPay && pathname !== '/staff/staff-pay') return '/staff/staff-pay';
            }
            return '/';
        }

        render() {
            const { s, ready } = this.state;
            if (!ready) {
                return <div className="reception-mode-checking" aria-busy="true" style={{ minHeight: '40vh' }} />;
            }
            if (hiddenIn(s)) {
                return (
                    <div className="reception-mode-restricted">
                        <h3>Not available in reception mode</h3>
                        <p>Unlock reception mode with the PIN to open this.</p>
                        <Redirect to={this.redirectTarget()} />
                    </div>
                );
            }
            return <WrappedComponent {...this.props} />;
        }
    }

    // Every store slice, as before: wrapped pages read their props from it.
    return connect((state) => ({ ...state }))(WithReceptionModeGuard);
};

export default ReceptionModeGuard;
